import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { TrendingUp as TrendingUpIcon, Visibility as VisibilityIcon, Close as CloseIcon } from '@mui/icons-material';

const WeeklyWinners = ({ topBusinesses = [], onViewAnalysis, loading = false }) => {
  const displayWinners = topBusinesses.slice(0, 5);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [businessDetails, setBusinessDetails] = useState(null);

  // State for activity detail modal
  const [activityDetailOpen, setActivityDetailOpen] = useState(false);
  const [selectedActivityType, setSelectedActivityType] = useState(null);
  const [activityDates, setActivityDates] = useState([]);

  const handleOpenModal = async (business) => {
    setSelectedBusiness(business);
    setModalOpen(true);
    setModalLoading(true);

    try {
      const token = localStorage.getItem('auth_token');

      // Fetch both keyword performance and activity summary
      const [keywordRes, activityRes] = await Promise.all([
        fetch(
          `/api/analytics/business-keyword-performance?businessName=${encodeURIComponent(business.businessName)}&months=0`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        ),
        fetch(
          `/api/analytics/business-activity-summary?businessName=${encodeURIComponent(business.businessName)}&months=0`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        )
      ]);

      if (keywordRes.ok) {
        const keywordData = await keywordRes.json();
        const activityData = activityRes.ok ? await activityRes.json() : null;

        setBusinessDetails({
          ...keywordData,
          createdAt: activityData?.createdAt || null,
          activity: activityData?.activity || {}
        });
      } else {
        console.error('Failed to fetch business details');
      }
    } catch (err) {
      console.error('Error fetching business details:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedBusiness(null);
    setBusinessDetails(null);
  };

  const handleOpenActivityDetail = async (activityType) => {
    let dates = [];
    let title = '';

    switch (activityType) {
      case 'posts':
        dates = (businessDetails?.activity?.posts || []).map(p => ({ date: new Date(p.createdAt), label: new Date(p.createdAt).toLocaleDateString('de-DE') }));
        title = 'Posts Veröffentlicht';
        break;
      case 'services_added':
        dates = (businessDetails?.activity?.services?.added || []).map(s => ({ date: new Date(s.createdAt), label: new Date(s.createdAt).toLocaleDateString('de-DE') }));
        title = 'Hinzugefügte Services';
        break;
      case 'services_updated':
        dates = (businessDetails?.activity?.services?.updated || []).map(s => ({ date: new Date(s.updatedAt), label: new Date(s.updatedAt).toLocaleDateString('de-DE') }));
        title = 'Aktualisierte Service-Beschreibungen';
        break;
      case 'keywords':
        dates = (businessDetails?.activity?.keywords || []).map(k => ({ date: new Date(k.createdAt), label: new Date(k.createdAt).toLocaleDateString('de-DE') }));
        title = 'Hinzugefügte Keywords';
        break;
      case 'media':
        // Use pre-filtered media data from backend (already filtered by business creation date)
        const allMediaItems = businessDetails?.activity?.media || [];

        // Group media by date
        const dateMap = new Map();
        allMediaItems.forEach(m => {
          const mediaDate = new Date(m.createdAt);
          const dateStr = mediaDate.toLocaleDateString('de-DE');
          if (!dateMap.has(dateStr)) {
            dateMap.set(dateStr, { date: mediaDate, count: 0 });
          }
          dateMap.get(dateStr).count += 1;
        });

        // Convert to array format, sorted newest first
        dates = Array.from(dateMap.values())
          .map(item => ({
            date: item.date,
            label: `${item.date.toLocaleDateString('de-DE')} (${item.count}x)`
          }))
          .sort((a, b) => b.date - a.date);

        title = 'Media hinzugefügt';
        break;
      default:
        break;
    }

    // Sort dates chronologically (newest first)
    dates.sort((a, b) => b.date - a.date);

    setSelectedActivityType(title);
    setActivityDates(dates);
    setActivityDetailOpen(true);
  };

  const handleCloseActivityDetail = () => {
    setActivityDetailOpen(false);
    setSelectedActivityType(null);
    setActivityDates([]);
  };

  return (
    <>
      <Card
        sx={{
          background: '#0A0A0B',
          border: 'none',
          borderRadius: '18px',
          padding: '24px',
          boxShadow: '0 1px 2px rgba(16,16,20,.06), 0 2px 6px rgba(16,16,20,.05)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}
      >
        {/* Header with Icon */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 700,
              color: '#FFFFFF',
              fontFamily: 'Plus Jakarta Sans, system-ui'
            }}
          >
            Leistungsträger
          </Typography>
          <TrendingUpIcon sx={{ color: '#C80F2E', fontSize: '18px' }} />
        </Box>

        {/* Winners List */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {loading ? (
            <Typography sx={{ color: '#888', fontSize: '14px' }}>Laden...</Typography>
          ) : displayWinners.length > 0 ? (
            displayWinners.map((winner, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '30px 1fr auto auto',
                  gap: '12px',
                  alignItems: 'center',
                  paddingY: '8px'
                }}
              >
                {/* Numbered Badge - Small Red */}
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#C80F2E',
                    fontFamily: 'Plus Jakarta Sans, system-ui'
                  }}
                >
                  #{idx + 1}
                </Typography>

                {/* Name */}
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#FFFFFF',
                    fontFamily: 'Plus Jakarta Sans, system-ui',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={winner.businessName}
                >
                  {winner.businessName}
                </Typography>

                {/* Average Improvement */}
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 700,
                    color: '#1E9E5A',
                    fontFamily: 'JetBrains Mono, monospace',
                    whiteSpace: 'nowrap'
                  }}
                >
                  +{winner.avgImprovement || 0}
                </Typography>

                {/* Eye Icon */}
                <IconButton
                  size="small"
                  onClick={() => handleOpenModal(winner)}
                  sx={{
                    color: '#888',
                    '&:hover': { color: '#FFFFFF' }
                  }}
                  title="Details anzeigen"
                >
                  <VisibilityIcon sx={{ fontSize: '18px' }} />
                </IconButton>
              </Box>
            ))
          ) : (
            <Typography sx={{ color: '#888', fontSize: '14px' }}>Keine Daten verfügbar</Typography>
          )}
        </Box>

        {/* Action Button */}
        <Button
          fullWidth
          onClick={onViewAnalysis}
          sx={{
            backgroundColor: 'transparent',
            color: '#FFFFFF',
            border: '1px solid rgba(255,255,255,0.15)',
            fontSize: '13px',
            fontWeight: 500,
            padding: '10px 12px',
            borderRadius: '10px',
            textTransform: 'none',
            marginTop: '4px',
            fontFamily: 'Plus Jakarta Sans, system-ui',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderColor: 'rgba(255,255,255,0.25)'
            }
          }}
        >
          Top-Performer-Analyse öffnen
        </Button>
      </Card>

      {/* Business Details Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            background: '#1a1a1b',
            color: '#FFFFFF',
            width: '90vw',
            height: '90vh',
            maxWidth: 'none',
            maxHeight: 'none',
            margin: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <Typography sx={{ fontWeight: 700,color: '#FFFFFF', fontSize: '16px' }}>
                                    {selectedBusiness?.businessName}
                                </Typography>
                                <IconButton size="small" onClick={handleCloseModal} sx={{ color: '#FFFFFF' }}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Typography sx={{ fontSize: '12px', color: '#888', fontFamily: 'JetBrains Mono, monospace' }}>
                                        Location ID:
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#FFA500', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
                                        {selectedBusiness?.googleLocationId}
                                    </Typography>
                                </Box>
                                <Box sx={{ width: '1px', height: '16px', background: '#3a3a3b' }} />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Typography sx={{ fontSize: '12px', color: '#888', fontFamily: 'JetBrains Mono, monospace' }}>
                                        Erstellt:
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#1E9E5A', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
                                        {businessDetails?.createdAt
                                            ? new Date(businessDetails.createdAt).toLocaleDateString('de-DE')
                                            : selectedBusiness?.createdAt
                                            ? new Date(selectedBusiness.createdAt).toLocaleDateString('de-DE')
                                            : 'N/A'}
                                    </Typography>
                                </Box>
                            </Box>
                        </DialogTitle>


        

        <DialogContent sx={{ pt: 3, px: 4, height: 'calc(100vh - 100px)', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          {modalLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : businessDetails?.keywords ? (
            <Box sx={{ mt: 0 }}>
              {/* Performance Overview Section */}
              <Box sx={{ mb: 4, p: 2.5, background: 'linear-gradient(135deg, #1E9E5A15 0%, #1E9E5A08 100%)', border: '1px solid #1E9E5A30', borderRadius: '12px' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                  <Box>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                      Durchschnittliche Verbesserung
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography sx={{ fontSize: '36px', fontWeight: 700, color: '#1E9E5A' }}>
                        +{businessDetails.avgImprovement || 0}%
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#888' }}>
                        über {businessDetails.keywordCount || 0} Keywords
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                      Keywords mit Fortschritt
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography sx={{ fontSize: '36px', fontWeight: 700, color: '#1E9E5A' }}>
                        {businessDetails.keywords.filter(k => k.improvement > 0).length}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#888' }}>
                        von {businessDetails.keywordCount || 0} Keywords
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Activity Timeline Section */}
              {businessDetails.activity && (
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                      Aktivität seit Erstellung
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#888' }}>
                      Maßnahmen die zur Leistung beitragen
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' }, gap: 1.5 }}>
                    {/* Posts */}
                    <Box sx={{
                      background: businessDetails.activity.postsAdded > 0 ? '#1a3a2e' : '#2a2a2b',
                      border: businessDetails.activity.postsAdded > 0 ? '1px solid #1E9E5A40' : '1px solid #3a3a3b',
                      p: 2,
                      borderRadius: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      minHeight: '110px',
                      transition: 'all 200ms ease',
                      '&:hover': {
                        background: businessDetails.activity.postsAdded > 0 ? '#1a4a36' : '#323232',
                        borderColor: businessDetails.activity.postsAdded > 0 ? '#1E9E5A60' : '#4a4a4a'
                      }
                    }}>
                      <Box>
                        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                          Beiträge
                        </Typography>
                        <Typography sx={{ fontSize: '28px', fontWeight: 700, color: businessDetails.activity.postsAdded > 0 ? '#1E9E5A' : '#555', mb: 0.5 }}>
                          {businessDetails.activity.postsAdded || 0}
                        </Typography>
                      </Box>
                      {businessDetails.activity.postsAdded > 0 && (
                        <IconButton size="small" onClick={() => handleOpenActivityDetail('posts')} sx={{ color: '#1E9E5A', alignSelf: 'flex-start', p: 0, fontSize: '14px', '&:hover': { color: '#FFFFFF' } }}>
                          <VisibilityIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                      )}
                    </Box>

                    {/* Services Added */}
                    <Box sx={{
                      background: businessDetails.activity.servicesAdded > 0 ? '#1a3a2e' : '#2a2a2b',
                      border: businessDetails.activity.servicesAdded > 0 ? '1px solid #1E9E5A40' : '1px solid #3a3a3b',
                      p: 2,
                      borderRadius: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      minHeight: '110px',
                      transition: 'all 200ms ease',
                      '&:hover': {
                        background: businessDetails.activity.servicesAdded > 0 ? '#1a4a36' : '#323232',
                        borderColor: businessDetails.activity.servicesAdded > 0 ? '#1E9E5A60' : '#4a4a4a'
                      }
                    }}>
                      <Box>
                        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                          Services
                        </Typography>
                        <Typography sx={{ fontSize: '28px', fontWeight: 700, color: businessDetails.activity.servicesAdded > 0 ? '#1E9E5A' : '#555', mb: 0.5 }}>
                          {businessDetails.activity.servicesAdded || 0}
                        </Typography>
                      </Box>
                      {businessDetails.activity.servicesAdded > 0 && (
                        <IconButton size="small" onClick={() => handleOpenActivityDetail('services_added')} sx={{ color: '#1E9E5A', alignSelf: 'flex-start', p: 0, fontSize: '14px', '&:hover': { color: '#FFFFFF' } }}>
                          <VisibilityIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                      )}
                    </Box>

                    {/* Media */}
                    <Box sx={{
                      background: businessDetails.activity.mediaAdded > 0 ? '#1a3a2e' : '#2a2a2b',
                      border: businessDetails.activity.mediaAdded > 0 ? '1px solid #1E9E5A40' : '1px solid #3a3a3b',
                      p: 2,
                      borderRadius: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      minHeight: '110px',
                      transition: 'all 200ms ease',
                      '&:hover': {
                        background: businessDetails.activity.mediaAdded > 0 ? '#1a4a36' : '#323232',
                        borderColor: businessDetails.activity.mediaAdded > 0 ? '#1E9E5A60' : '#4a4a4a'
                      }
                    }}>
                      <Box>
                        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                          Medien
                        </Typography>
                        <Typography sx={{ fontSize: '28px', fontWeight: 700, color: businessDetails.activity.mediaAdded > 0 ? '#1E9E5A' : '#555', mb: 0.5 }}>
                          {businessDetails.activity.mediaAdded || 0}
                        </Typography>
                      </Box>
                      {businessDetails.activity.mediaAdded > 0 && (
                        <IconButton size="small" onClick={() => handleOpenActivityDetail('media')} sx={{ color: '#1E9E5A', alignSelf: 'flex-start', p: 0, fontSize: '14px', '&:hover': { color: '#FFFFFF' } }}>
                          <VisibilityIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                      )}
                    </Box>

                    {/* Keywords */}
                    <Box sx={{
                      background: businessDetails.activity.keywordsAdded > 0 ? '#1a3a2e' : '#2a2a2b',
                      border: businessDetails.activity.keywordsAdded > 0 ? '1px solid #1E9E5A40' : '1px solid #3a3a3b',
                      p: 2,
                      borderRadius: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      minHeight: '110px',
                      transition: 'all 200ms ease',
                      '&:hover': {
                        background: businessDetails.activity.keywordsAdded > 0 ? '#1a4a36' : '#323232',
                        borderColor: businessDetails.activity.keywordsAdded > 0 ? '#1E9E5A60' : '#4a4a4a'
                      }
                    }}>
                      <Box>
                        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                          Keywords
                        </Typography>
                        <Typography sx={{ fontSize: '28px', fontWeight: 700, color: businessDetails.activity.keywordsAdded > 0 ? '#1E9E5A' : '#555', mb: 0.5 }}>
                          {businessDetails.activity.keywordsAdded || 0}
                        </Typography>
                      </Box>
                      {businessDetails.activity.keywordsAdded > 0 && (
                        <IconButton size="small" onClick={() => handleOpenActivityDetail('keywords')} sx={{ color: '#1E9E5A', alignSelf: 'flex-start', p: 0, fontSize: '14px', '&:hover': { color: '#FFFFFF' } }}>
                          <VisibilityIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                      )}
                    </Box>

                    {/* Service Updates */}
                    <Box sx={{
                      background: businessDetails.activity.servicesUpdated > 0 ? '#2a2a2b' : '#1a1a1b',
                      border: businessDetails.activity.servicesUpdated > 0 ? '1px solid #FFA50040' : '1px solid #3a3a3b',
                      p: 2,
                      borderRadius: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      minHeight: '110px',
                      transition: 'all 200ms ease',
                      '&:hover': {
                        background: businessDetails.activity.servicesUpdated > 0 ? '#332211' : '#2a2a2b',
                        borderColor: businessDetails.activity.servicesUpdated > 0 ? '#FFA50060' : '#4a4a4a'
                      }
                    }}>
                      <Box>
                        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                          Service-Updates
                        </Typography>
                        <Typography sx={{ fontSize: '28px', fontWeight: 700, color: businessDetails.activity.servicesUpdated > 0 ? '#FFA500' : '#555', mb: 0.5 }}>
                          {businessDetails.activity.servicesUpdated || 0}
                        </Typography>
                      </Box>
                      {businessDetails.activity.servicesUpdated > 0 && (
                        <IconButton size="small" onClick={() => handleOpenActivityDetail('services_updated')} sx={{ color: '#FFA500', alignSelf: 'flex-start', p: 0, fontSize: '14px', '&:hover': { color: '#FFFFFF' } }}>
                          <VisibilityIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}


             

              <Box sx={{ mb: 0 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                    Detaillierte Keyword-Analyse
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#888' }}>
                    Ranking-Entwicklung und Performance-Tracking
                  </Typography>
                </Box>

                {/* Summary Stats - Enhanced */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
                  <Box sx={{ background: '#1a3a2e', p: 2, borderRadius: '10px', border: '1px solid #1E9E5A30' }}>
                    <Typography sx={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>Analysen pro Keyword</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#1E9E5A' }}>
                        {businessDetails.keywords.length > 0
                          ? (businessDetails.keywords.reduce((sum, k) => sum + (k.snapshotCount || 0), 0) / businessDetails.keywords.length).toFixed(1)
                          : 0}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#888' }}>durchschnittlich</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ background: '#1a3a2e', p: 2, borderRadius: '10px', border: '1px solid #1E9E5A30' }}>
                    <Typography sx={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>Verbessert</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#1E9E5A' }}>
                        {businessDetails.keywords.filter(k => k.improvement > 0).length}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#888' }}>/ {businessDetails.keywords.length}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ background: '#2a2a2b', p: 2, borderRadius: '10px', border: '1px solid #3a3a3b' }}>
                    <Typography sx={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>Stabil</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#888' }}>
                        {businessDetails.keywords.filter(k => k.improvement === 0).length}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#666' }}>/ {businessDetails.keywords.length}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ background: '#2a1a1a', p: 2, borderRadius: '10px', border: '1px solid #ef534050' }}>
                    <Typography sx={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>Rückgang</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#ef5350' }}>
                        {businessDetails.keywords.filter(k => k.improvement < 0).length}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#666' }}>/ {businessDetails.keywords.length}</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Keywords Grid View - Creative Layout */}
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 2 }}>
                    Top Keywords nach Verbesserung
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                    {businessDetails.keywords
                      .filter(k => k.improvement > 0)
                      .sort((a, b) => b.improvement - a.improvement)
                      .slice(0, 6)
                      .map((kw, idx) => {
                        const rankChange = (kw.firstSnapshotAvgRank || 0) - (kw.lastSnapshotAvgRank || 0);
                        const rankPercentage = kw.firstSnapshotAvgRank ? Math.min(100, (rankChange / (kw.firstSnapshotAvgRank || 1)) * 100) : 0;
                        return (
                          <Box
                            key={idx}
                            sx={{
                              background: 'linear-gradient(135deg, #1a3a2e 0%, #1a2a2b 100%)',
                              border: '1px solid #1E9E5A30',
                              borderRadius: '10px',
                              p: 2,
                              transition: 'all 200ms ease',
                              position: 'relative',
                              '&:hover': {
                                border: '1px solid #1E9E5A60',
                                boxShadow: '0 0 12px rgba(30, 158, 90, 0.15)'
                              }
                            }}
                          >

                            {/* Two Column Layout: Stats Left, Graph Right - Full Height */}
                            <Box sx={{ display: 'flex', gap: 2, minHeight: '120px' }}>
                              {/* LEFT SIDE - STATS */}
                              <Box sx={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: '140px' }}>
                                {/* Top - Keyword and Range */}
                                <Box>
                                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#FFFFFF', mb: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {kw.keyword}
                                  </Typography>
                                  
                                </Box>

                                {/* Stats Column - Best Rank, Count, Duration, Avg Rank */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1 }}>
                                  {/* Best Rank */}
                                  <Box sx={{ background: '#2a2a2b', p: 0.75, borderRadius: '6px', border: '1px solid #3a3a3b', minWidth: 'fit-content' }}>
                                    <Typography sx={{ fontSize: '7px', color: '#888', mb: 0.25, textTransform: 'uppercase', letterSpacing: '0.02em' }}>Best</Typography>
                                    <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#9c9c9c' }}>
                                      #{kw.firstSnapshotBestRank || 'N/A'} → #{kw.lastSnapshotBestRank || 'N/A'}
                                    </Typography>
                                  </Box>

                                    {/* Avg Rank */}
                                  <Box sx={{ background: '#2a2a2b', p: 0.75, borderRadius: '6px', border: '1px solid #3a3a3b', minWidth: 'fit-content' }}>
                                    <Typography sx={{ fontSize: '7px', color: '#888', mb: 0.25, textTransform: 'uppercase', letterSpacing: '0.02em' }}>Ø Rang</Typography>
                                    <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#9c9c9c' }}>
                                      #{kw.firstSnapshotAvgRank || 'N/A'} → #{kw.lastSnapshotAvgRank || 'N/A'}
                                    </Typography>
                                  </Box>

                                  {/* Count and Duration Row */}
                                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                                    {/* Snapshot Count */}
                                    <Box sx={{ background: '#1E9E5A20', border: '1px solid #1E9E5A40', px: 1, py: 0.5, borderRadius: '4px', minWidth: 'fit-content' }}>
                                      <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#1E9E5A' }}>
                                        {kw.snapshotCount || 0}x
                                      </Typography>
                                    </Box>

                                    {/* Time Span */}
                                    <Box sx={{ background: '#88888820', border: '1px solid #88888840', px: 1, py: 0.5, borderRadius: '4px', minWidth: 'fit-content' }}>
                                      <Typography sx={{ fontSize: '8px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                                        {kw.firstSnapshotDate && kw.lastSnapshotDate
                                          ? (() => {
                                            const days = Math.ceil((new Date(kw.lastSnapshotDate) - new Date(kw.firstSnapshotDate)) / (1000 * 60 * 60 * 24));
                                            const months = Math.floor(days / 30);
                                            const remainingDays = days % 30;
                                            return months > 0 ? `${months}M ${remainingDays}d` : `${days}d`;
                                          })()
                                          : 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Box>

                                
                                </Box>

                                {/* Bottom - Progress Bar Full Width */}
                                <Box sx={{ width: '100%' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: '8px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                      Verb.
                                    </Typography>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#1E9E5A' }}>
                                      +{kw.improvement}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ background: '#2a2a2b', height: '5px', borderRadius: '3px', overflow: 'hidden', border: '1px solid #3a3a3b' }}>
                                    <Box sx={{
                                      height: '100%',
                                      background: 'linear-gradient(90deg, #1E9E5A 0%, #1E9E5A80 100%)',
                                      width: `${Math.min(100, (kw.improvement / 20) * 100)}%`,
                                      transition: 'width 500ms ease-out'
                                    }} />
                                  </Box>
                                </Box>
                              </Box>

                              {/* RIGHT SIDE - INTERACTIVE GRAPH FULL HEIGHT */}
                              {kw.trend && kw.trend.length > 0 && (() => {
                                const trendData = kw.trend.sort((a, b) => new Date(a.date) - new Date(b.date));
                                const minRank = Math.min(...trendData.map(t => t.rank || 100));
                                const maxRank = Math.max(...trendData.map(t => t.rank || 1));

                                // Chart dimensions - Full height
                                const chartWidth = 600;
                                const chartHeight = 180;
                                const margin = { top: 12, right: 12, bottom: 50, left: 45 };
                                const plotWidth = chartWidth - margin.left - margin.right;
                                const plotHeight = chartHeight - margin.top - margin.bottom;

                                const points = trendData.map((point, idx) => {
                                  const x = margin.left + (idx / Math.max(1, trendData.length - 1)) * plotWidth;
                                  const normalizedRank = maxRank === minRank ? 0.5 : (point.rank - minRank) / (maxRank - minRank);
                                  const y = margin.top + (normalizedRank * plotHeight);
                                  return { x, y, rank: point.rank, date: new Date(point.date) };
                                });

                                const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

                                // Show all dates on x-axis
                                const dateIndices = trendData.map((_, i) => i);

                                return (
                                  <Box sx={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                      background: '#2a2a2b',
                                      borderRadius: '8px',
                                      border: '1px solid #3a3a3b',
                                      p: 1,
                                      position: 'relative',
                                      width: '100%',
                                      height: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      '&:hover': {
                                        border: '1px solid #1E9E5A40'
                                      }
                                    }}>
                                      <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={{ display: 'block' }}>
                                        {/* Grid lines */}
                                        <defs>
                                          <pattern id={`grid-${idx}`} width={plotWidth / 3} height={plotHeight / 3} patternUnits="userSpaceOnUse" x={margin.left} y={margin.top}>
                                            <path d={`M ${plotWidth / 3} 0 L 0 0 0 ${plotHeight / 3}`} fill="none" stroke="#3a3a3b" strokeWidth="0.3" opacity="0.2" />
                                          </pattern>
                                        </defs>
                                        <rect x={margin.left} y={margin.top} width={plotWidth} height={plotHeight} fill={`url(#grid-${idx})`} />

                                        {/* Y-axis */}
                                        <line x1={margin.left - 2} y1={margin.top} x2={margin.left - 2} y2={margin.top + plotHeight} stroke="#3a3a3b" strokeWidth="0.5" />

                                        {/* Y-axis labels */}
                                        <text x={margin.left - 6} y={margin.top + 3} fontSize="10" fill="#666" textAnchor="end">{Math.round(minRank)}</text>
                                        <text x={margin.left - 6} y={margin.top + plotHeight + 2} fontSize="10" fill="#666" textAnchor="end">{Math.round(maxRank)}</text>

                                        {/* Trend line */}
                                        <path
                                          d={pathData}
                                          fill="none"
                                          stroke="#1E9E5A"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />

                                        {/* All data points - Large and visible */}
                                        {points.map((point, pidx) => (
                                          <g key={`point-${pidx}`}>
                                            {/* Glow effect */}
                                            <circle
                                              cx={point.x}
                                              cy={point.y}
                                              r="3.5"
                                              fill="#1E9E5A"
                                              opacity="0.15"
                                            />
                                            {/* Main point */}
                                            <circle
                                              cx={point.x}
                                              cy={point.y}
                                              r="2.5"
                                              fill={pidx === 0 ? '#9c9c9c' : '#1E9E5A'}
                                              stroke="#1a1a1b"
                                              strokeWidth="1.5"
                                            />
                                            {/* Highlight dot */}
                                            <circle
                                              cx={point.x}
                                              cy={point.y}
                                              r="1"
                                              fill="#FFFFFF"
                                              opacity="0.8"
                                            />
                                          </g>
                                        ))}

                                        {/* X-axis date labels - All 9 dates */}
                                        {dateIndices.map((dateIdx) => {
                                          const point = points[dateIdx];
                                          const date = trendData[dateIdx];
                                          const dateStr = new Date(date.date).toLocaleDateString('de-DE', { month: '2-digit', day: '2-digit', year: '2-digit' });
                                          return (
                                            <g key={dateIdx}>
                                              <line x1={point.x} y1={margin.top + plotHeight} x2={point.x} y2={margin.top + plotHeight + 2} stroke="#3a3a3b" strokeWidth="0.5" />
                                              <text x={point.x} y={margin.top + plotHeight + 18} fontSize="9" fill="#666" textAnchor="middle">{dateStr}</text>
                                            </g>
                                          );
                                        })}

                                        {/* Interactive hover rects */}
                                        {points.map((point, pidx) => (
                                          <rect
                                            key={`hover-${pidx}`}
                                            x={point.x - 8}
                                            y={margin.top}
                                            width="16"
                                            height={plotHeight}
                                            fill="transparent"
                                            style={{ cursor: 'pointer' }}
                                            onMouseEnter={(e) => {
                                              const parent = e.currentTarget.parentElement;
                                              const tooltips = parent.querySelectorAll('g[data-tooltip]');
                                              tooltips.forEach((t, i) => {
                                                if (i === pidx) t.style.opacity = '1';
                                              });
                                            }}
                                            onMouseLeave={(e) => {
                                              const parent = e.currentTarget.parentElement;
                                              const tooltips = parent.querySelectorAll('g[data-tooltip]');
                                              tooltips.forEach((t) => {
                                                t.style.opacity = '0';
                                              });
                                            }}
                                          />
                                        ))}

                                        {/* Hover tooltips - Smart positioning */}
                                        {points.map((point, pidx) => {
                                          const snapshotData = trendData[pidx];
                                          const bestRankUpToNow = Math.min(...trendData.slice(0, pidx + 1).map(t => t.rank || 100));
                                          const dateStr = point.date.toLocaleDateString('de-DE', { month: '2-digit', day: '2-digit', year: '2-digit' });

                                          // Smart positioning - show below if near top, above if near bottom
                                          const tooltipHeight = 40;
                                          const tooltipWidth = 100;
                                          const showBelow = point.y < margin.top + 35;
                                          const tooltipX = Math.max(margin.left + 15, Math.min(point.x - tooltipWidth / 2, chartWidth - margin.right - 15));
                                          const tooltipY = showBelow ? point.y + 8 : point.y - tooltipHeight - 8;

                                          return (
                                            <g key={`tooltip-${pidx}`} data-tooltip="true" style={{ opacity: '0', transition: 'opacity 150ms ease', pointerEvents: 'none' }}>
                                              {/* Tooltip background */}
                                              <rect
                                                x={tooltipX}
                                                y={tooltipY}
                                                width={tooltipWidth}
                                                height={tooltipHeight}
                                                fill="#1E9E5A"
                                                rx="3"
                                              />
                                              {/* Arrow pointer */}
                                              {showBelow ? (
                                                <polygon
                                                  points={`${point.x},${point.y + 8} ${point.x - 3},${point.y + 2} ${point.x + 3},${point.y + 2}`}
                                                  fill="#1E9E5A"
                                                />
                                              ) : (
                                                <polygon
                                                  points={`${point.x},${point.y - 8} ${point.x - 3},${point.y - 2} ${point.x + 3},${point.y - 2}`}
                                                  fill="#1E9E5A"
                                                />
                                              )}
                                              {/* Date */}
                                              <text
                                                x={tooltipX + tooltipWidth / 2}
                                                y={tooltipY + 10}
                                                fontSize="9"
                                                fill="#FFFFFF"
                                                fontWeight="700"
                                                textAnchor="middle"
                                              >
                                                {dateStr}
                                              </text>
                                              {/* Divider */}
                                              <line
                                                x1={tooltipX + 5}
                                                y1={tooltipY + 14}
                                                x2={tooltipX + tooltipWidth - 5}
                                                y2={tooltipY + 14}
                                                stroke="#ffffff60"
                                                strokeWidth="0.5"
                                              />
                                              {/* Average Rank */}
                                              <text
                                                x={tooltipX + 5}
                                                y={tooltipY + 23}
                                                fontSize="8"
                                                fill="#FFFFFF"
                                                fontWeight="600"
                                              >
                                                Ø:
                                              </text>
                                              <text
                                                x={tooltipX + tooltipWidth - 5}
                                                y={tooltipY + 23}
                                                fontSize="8"
                                                fill="#FFFFFF"
                                                fontWeight="700"
                                                textAnchor="end"
                                              >
                                                #{Math.round(point.rank)}
                                              </text>
                                              {/* Best Rank */}
                                              <text
                                                x={tooltipX + 5}
                                                y={tooltipY + 32}
                                                fontSize="8"
                                                fill="#FFFFFF"
                                                fontWeight="600"
                                              >
                                                Best:
                                              </text>
                                              <text
                                                x={tooltipX + tooltipWidth - 5}
                                                y={tooltipY + 32}
                                                fontSize="8"
                                                fill="#FFFFFF"
                                                fontWeight="700"
                                                textAnchor="end"
                                              >
                                                #{Math.round(bestRankUpToNow)}
                                              </text>
                                              {/* Snapshot # */}
                                              <text
                                                x={tooltipX + tooltipWidth / 2}
                                                y={tooltipY + 40}
                                                fontSize="8"
                                                fill="#ffffff80"
                                                fontWeight="600"
                                                textAnchor="middle"
                                              >
                                                {pidx + 1}/9
                                              </text>
                                            </g>
                                          );
                                        })}
                                      </svg>
                                    </Box>
                                  </Box>
                                );
                              })()}
                            </Box>
                          </Box>
                        );
                      })}
                  </Box>
                </Box>

               
              </Box>
            </Box>
          ) : (
            <Typography sx={{ color: '#888' }}>Keine Keywords gefunden</Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} sx={{ color: '#FFFFFF' }}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activity Detail Modal - showing dates in grid */}
      <Dialog
        open={activityDetailOpen}
        onClose={handleCloseActivityDetail}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: '#1a1a1b',
            color: '#FFFFFF'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 700, color: '#FFFFFF', fontSize: '16px' }}>
            {selectedActivityType}
          </Typography>
          <IconButton size="small" onClick={handleCloseActivityDetail} sx={{ color: '#FFFFFF' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 1 }}>
            {activityDates.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  background: '#2a2a2b',
                  p: 1.5,
                  borderRadius: '8px',
                  textAlign: 'center',
                  border: '1px solid #3a3a3b',
                  '&:hover': { background: '#3a3a3b' }
                }}
              >
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1E9E5A' }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseActivityDetail} sx={{ color: '#FFFFFF' }}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WeeklyWinners;
